/**
 * Auth context : état utilisateur + tenant + actions login/logout/register.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type {
  User,
  Tenant,
  LoginRequest,
  RegisterRequest,
  RegisterAdministreRequest,
  RegisterAdministreResponse,
  VerifyEmailResponse,
  UpdateMyProfileRequest,
} from '@roronoa-collectivites/shared-types';
import { api, setAccessToken, getAccessToken } from './api';

interface ImpersonationState {
  /** Email du platform_admin (acteur réel, même pendant impersonation). */
  actorEmail: string;
  /** Nom du tenant cible (pour la bannière rouge). */
  targetTenantNom: string;
  /** Date d'expiration ISO du token impersonation (15 min). */
  expiresAt: string;
}

interface AuthContextValue {
  user: User | null;
  tenant: Tenant | null;
  loading: boolean;
  /**
   * L13.14 — rôle natif de l'utilisateur, hors promotion temporaire.
   * Si l'utilisateur a été promu via un override, `user.role` reflète le rôle
   * promu mais `baseRole` reste le rôle d'origine. Sert à masquer les actions
   * de gouvernance (promouvoir/rétrograder) à un utilisateur lui-même promu.
   */
  baseRole: User['role'] | null;
  /** État impersonation actif. NULL si on est en mode normal. */
  impersonation: ImpersonationState | null;
  login: (input: LoginRequest) => Promise<void>;
  register: (input: RegisterRequest) => Promise<void>;
  /** Passe 2A.1 — inscription administré (pas d'auth auto : vérif email pending). */
  registerAdministre: (input: RegisterAdministreRequest) => Promise<RegisterAdministreResponse>;
  /** Passe 2A.1 — demande de reset (anti-énum, toujours OK). */
  forgotPassword: (email: string) => Promise<void>;
  /** Passe 2A.1 — applique un nouveau mdp avec un token de reset valide. */
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  /** Passe 2A.1 — consomme un token de vérification email. */
  verifyEmail: (token: string) => Promise<VerifyEmailResponse>;
  /** Passe 2A.1 — renvoie un mail de vérification. */
  resendVerification: (email: string) => Promise<void>;
  /** Bloc A.2 — démarre une session hotline plateforme sur un tenant. */
  startImpersonation: (tenantId: string) => Promise<void>;
  /** Bloc A.2 — sort du mode hotline et restaure le token original du platform_admin. */
  exitImpersonation: () => Promise<void>;
  /** Bloc B — modifier son profil. */
  updateMyProfile: (input: UpdateMyProfileRequest) => Promise<void>;
  /** Bloc B — changer son mot de passe. */
  changeMyPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Bloc B — export RGPD (déclenche un téléchargement). */
  exportMyData: () => Promise<void>;
  /** Bloc B — supprimer son compte (anonymisation RGPD). Logout local après. */
  deleteMyAccount: () => Promise<void>;
  /** Bloc B — révoquer toutes les sessions (force logout). */
  logoutAllSessions: () => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  // L13.14 — rôle natif (hors override). Peuplé par /me, qui seul le renvoie.
  const [baseRole, setBaseRole] = useState<User['role'] | null>(null);

  /** Token original du platform_admin, sauvegardé pendant la session hotline. */
  const originalTokenRef = useRef<string | null>(null);
  const originalUserRef = useRef<User | null>(null);
  const originalTenantRef = useRef<Tenant | null>(null);

  // Au montage : tenter un refresh pour récupérer l'access token, puis /me
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refreshed = await api.auth.refresh();
        if (cancelled) return;
        setAccessToken(refreshed.accessToken);
        setUser(refreshed.user);
        setTenant(refreshed.tenant);
        // /me renvoie le baseRole ET le user complet (avec servicesAdditionnels,
        // que le refresh ne fournit pas). On réhydrate le user pour que le cumul
        // de services soit pris en compte côté UI.
        try {
          const me = await api.auth.me();
          if (!cancelled) {
            setBaseRole(me.baseRole ?? me.user.role);
            setUser(me.user);
          }
        } catch {
          if (!cancelled) setBaseRole(refreshed.user.role);
        }
      } catch {
        // pas de session active, ok
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (input: LoginRequest) => {
    const res = await api.auth.login(input);
    setAccessToken(res.accessToken);
    setUser(res.user);
    setTenant(res.tenant);
    setBaseRole(res.user.role);
    // Réhydrate depuis /me pour récupérer servicesAdditionnels (cumul), que le
    // login ne renvoie pas. Sans ça, les briques cochées en cumul restent fermées.
    try {
      const me = await api.auth.me();
      setUser(me.user);
      setBaseRole(me.baseRole ?? me.user.role);
    } catch { /* le user de login suffit en secours */ }
  }, []);

  const register = useCallback(async (input: RegisterRequest) => {
    const res = await api.auth.register(input);
    setAccessToken(res.accessToken);
    setUser(res.user);
    setTenant(res.tenant);
  }, []);

  // ---------- Passe 2A.1 ----------

  const registerAdministre = useCallback(
    async (input: RegisterAdministreRequest): Promise<RegisterAdministreResponse> => {
      // Pas d'auth auto : on retourne la réponse au composant qui affiche
      // l'écran "vérifie ton email".
      return api.auth.registerAdministre(input);
    },
    []
  );

  const forgotPassword = useCallback(async (email: string) => {
    await api.auth.forgotPassword({ email });
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    await api.auth.resetPassword({ token, newPassword });
  }, []);

  const verifyEmail = useCallback(async (token: string): Promise<VerifyEmailResponse> => {
    return api.auth.verifyEmail(token);
  }, []);

  const resendVerification = useCallback(async (email: string) => {
    await api.auth.resendVerification({ email });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
      setTenant(null);
      setBaseRole(null); // L13.14
      // R13.6 — purger les sessionStorage liés à l'user (bandeau alerte dismissed, etc.)
      // pour ne pas qu'un user A laisse son état "dismiss" actif pour un user B
      // qui se connecterait dans le même onglet.
      try {
        const keys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.startsWith('vpc:alerte-dismissed')) keys.push(k);
        }
        keys.forEach((k) => sessionStorage.removeItem(k));
      } catch { /* ignore */ }
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const res = await api.auth.me();
    setUser(res.user);
    setTenant(res.tenant);
    setBaseRole(res.baseRole ?? res.user.role); // L13.14
  }, []);

  // ---------- Bloc A.2 — Mode plateforme hotline ----------

  const startImpersonation = useCallback(async (tenantId: string) => {
    // Sauvegarde l'état actuel (le platform_admin) avant de basculer
    const currentToken = getAccessToken();
    originalTokenRef.current = currentToken;
    originalUserRef.current = user;
    originalTenantRef.current = tenant;

    const res = await api.platform.tenants.impersonate(tenantId);
    // Bascule sur le token d'impersonation
    setAccessToken(res.impersonationToken);
    setImpersonation({
      actorEmail: res.actor.email,
      targetTenantNom: res.tenant.nom,
      expiresAt: res.expiresAt,
    });
    // Re-fetch /me avec le nouveau token pour récupérer le tenant cible
    try {
      const me = await api.auth.me();
      setUser(me.user);
      setTenant(me.tenant);
    } catch {
      // Si /me échoue on annule la bascule
      if (originalTokenRef.current) setAccessToken(originalTokenRef.current);
      setImpersonation(null);
      throw new Error('Impossible de récupérer le profil du tenant cible');
    }
  }, [user, tenant]);

  const exitImpersonation = useCallback(async () => {
    // Restaure le token et l'état originaux du platform_admin
    if (originalTokenRef.current) {
      setAccessToken(originalTokenRef.current);
      setUser(originalUserRef.current);
      setTenant(originalTenantRef.current);
    } else {
      // Pas de token original → on déconnecte par sécurité
      setAccessToken(null);
      setUser(null);
      setTenant(null);
    }
    originalTokenRef.current = null;
    originalUserRef.current = null;
    originalTenantRef.current = null;
    setImpersonation(null);
  }, []);

  // ---------- Bloc B — Mon compte self-service ----------

  const updateMyProfile = useCallback(async (input: UpdateMyProfileRequest) => {
    const res = await api.auth.updateMyProfile(input);
    setUser(res.user);
    setTenant(res.tenant);
  }, []);

  const changeMyPassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await api.auth.changeMyPassword({ currentPassword, newPassword });
  }, []);

  const exportMyData = useCallback(async () => {
    await api.auth.exportMyData();
  }, []);

  const deleteMyAccount = useCallback(async () => {
    await api.auth.deleteMyAccount();
    // Logout local — le compte est anonymisé côté serveur, on coupe la session client
    setAccessToken(null);
    setUser(null);
    setTenant(null);
  }, []);

  const logoutAllSessions = useCallback(async () => {
    await api.auth.logoutAll();
    // Le serveur a clearcookie. On coupe aussi côté client.
    setAccessToken(null);
    setUser(null);
    setTenant(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        loading,
        baseRole,
        impersonation,
        login,
        register,
        registerAdministre,
        forgotPassword,
        resetPassword,
        verifyEmail,
        resendVerification,
        startImpersonation,
        exitImpersonation,
        updateMyProfile,
        changeMyPassword,
        exportMyData,
        deleteMyAccount,
        logoutAllSessions,
        logout,
        refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
